// The Forecasting tab's Email view (2026-10-27, Patrick's ask): actuals only for now.
//   1. Volume    one weekly line, CXHub email plus the Bright Pattern Amazon queues,
//                with the split shown.
//   2. Staffing  handle time, the FTE the work asks for on his 23% method, the
//                email-designated schedule and the gap.
//   3. Service   each account's average time to first answer against its target.
// All the arithmetic is on the server (server/src/services/forecasting-email.js);
// this only lays it out. Hours sit behind a toggle, as on the Phone and Chat view.
import { useEffect, useState } from 'react'
import {
  Alert, Box, CircularProgress, FormControlLabel, Paper, Switch, Table, TableBody, TableHead, TableRow,
} from '@mui/material'
import { fetchForecastEmail } from '../api.js'
import SectionHeading from './SectionHeading.jsx'
import { Cell, Head, WeekCell, Fte, Divider, rowSx } from './ForecastTableParts.jsx'
import { formatCount, formatGap, formatSeconds, gapTone, EM_DASH } from '../forecastPhoneChat.js'
import { formatAvgHours, formatIndex, formatShare, slaHeader, slaTone } from '../forecastEmail.js'

const TABLE_BOX = { width: 'fit-content', maxWidth: '100%' }

function VolumeTable({ report }) {
  const forecast = report.forecast?.weeks ?? []
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', ...TABLE_BOX }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head align="left">Week ending</Head>
            <Head left>CXHub email</Head>
            <Head>Bright Pattern (Amazon, Spanish)</Head>
            <Head left>Total</Head>
            <Head>Bright Pattern share</Head>
            <Head left>Emails handled (CXHub + Bright Pattern)</Head>
            <Head left>Holiday ×</Head>
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={7}>Recent weeks · oldest first, then this week so far</Divider>
          {report.weeks.map((row) => {
            const v = row.volume
            const partial = row.isPartial
            return (
              <TableRow key={row.week} sx={rowSx(row)}>
                <WeekCell row={row} />
                <Cell left muted={v.cxhub == null || partial} italic={partial}>{formatCount(v.cxhub)}</Cell>
                <Cell muted={v.bpAmazon == null || partial} italic={partial}>{formatCount(v.bpAmazon)}</Cell>
                <Cell left bold muted={partial} italic={partial}>{formatCount(v.total)}{partial && v.total != null ? ' so far' : ''}</Cell>
                <Cell muted>{formatShare(v.bpShare)}</Cell>
                <Cell left muted={v.handledByPeople == null || partial} italic={partial}>{formatCount(v.handledByPeople)}</Cell>
                <Cell left muted>{EM_DASH}</Cell>
              </TableRow>
            )
          })}
          {forecast.length > 0 && (
            <>
              <Divider colSpan={7}>Forecast · this week in full, then the weeks ahead</Divider>
              {forecast.map((row) => {
                const v = row.volume
                return (
                  <TableRow key={row.week} sx={rowSx(row)}>
                    <WeekCell row={row} />
                    <Cell left italic>{formatCount(v.cxhub)}</Cell>
                    <Cell italic>{formatCount(v.bpAmazon)}</Cell>
                    <Cell left bold italic>{formatCount(v.total)}</Cell>
                    <Cell muted italic>{formatShare(v.bpShare)}</Cell>
                    <Cell left italic>{formatCount(v.handledByPeople)}</Cell>
                    <Cell left italic bold={row.seasonalIndex != null && row.seasonalIndex !== 1}>{formatIndex(row.seasonalIndex)}</Cell>
                  </TableRow>
                )
              })}
            </>
          )}
        </TableBody>
      </Table>
    </Paper>
  )
}

// The holiday multipliers in use and the three years they come from, for reference.
export function MultiplierTable({ rows }) {
  const years = Object.keys(rows[0]?.years ?? {})
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', ...TABLE_BOX, mt: 0.75 }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head align="left">Week ending</Head>
            <Head align="left">Week</Head>
            <Head left>Used</Head>
            {years.map((y, i) => <Head key={y} left={i === 0}>{y}</Head>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.week}>
              <WeekCell row={{ week: r.week }} />
              <Cell align="left">{r.label}</Cell>
              <Cell left bold tone={r.used !== 1 ? 'primary' : null}>{formatIndex(r.used)}</Cell>
              {years.map((y, i) => <Cell key={y} left={i === 0} muted>{formatIndex(r.years[y])}</Cell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

function StaffingTable({ report, showHours }) {
  const a = report.assumptions
  const forecast = report.forecast?.weeks ?? []
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', ...TABLE_BOX }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head rowSpan={2} align="left">Week ending</Head>
            <Head colSpan={5} left>Handle time</Head>
            <Head rowSpan={2} left>FTE needed (× {1 + a.shrinkageRate})</Head>
            <Head rowSpan={2} left>FTE scheduled (÷ {a.scheduledHoursPerFte})</Head>
            <Head rowSpan={2} left>Gap</Head>
          </TableRow>
          <TableRow>
            <Head left>CXHub, claims capped at {a.ahtCapMin} min</Head>
            <Head>CXHub, no cap</Head>
            <Head>Claims over {a.ahtCapMin} min</Head>
            <Head>Bright Pattern ÷ {a.bpInFocusDivisor}</Head>
            <Head>All</Head>
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={9}>Recent weeks · oldest first, then this week so far</Divider>
          {report.weeks.map((row) => {
            const p = row.plan
            const partial = row.isPartial
            return (
              <TableRow key={row.week} sx={rowSx(row)}>
                <WeekCell row={row} />
                <Cell left muted={row.aht.cxhubSec == null}>{formatSeconds(row.aht.cxhubSec)}</Cell>
                <Cell muted={row.aht.cxhubUncappedSec == null}>{formatSeconds(row.aht.cxhubUncappedSec)}</Cell>
                <Cell muted={row.aht.longClaims == null}>{row.aht.longClaims == null ? EM_DASH : row.aht.longClaims}</Cell>
                <Cell muted={row.aht.bpAmazonSec == null}>{formatSeconds(row.aht.bpAmazonSec)}</Cell>
                <Cell muted={row.aht.combinedSec == null}>{formatSeconds(row.aht.combinedSec)}</Cell>
                <Cell left bold muted={!p.need || partial} italic={partial}><Fte value={p.need} showHours={showHours} /></Cell>
                <Cell left bold muted={!p.scheduled}><Fte value={p.scheduled} showHours={showHours} /></Cell>
                <Cell left bold tone={gapTone(p.gap)} muted={p.gap == null}>{formatGap(p.gap)}</Cell>
              </TableRow>
            )
          })}
          {forecast.length > 0 && (
            <>
              <Divider colSpan={9}>Forecast · this week in full, then the weeks ahead</Divider>
              {forecast.map((row) => {
                const p = row.plan
                return (
                  <TableRow key={row.week} sx={rowSx(row)}>
                    <WeekCell row={row} />
                    <Cell left italic muted={row.aht.cxhubSec == null}>{formatSeconds(row.aht.cxhubSec)}</Cell>
                    <Cell italic muted>{EM_DASH}</Cell>
                    <Cell italic muted>{EM_DASH}</Cell>
                    <Cell italic muted={row.aht.bpAmazonSec == null}>{formatSeconds(row.aht.bpAmazonSec)}</Cell>
                    <Cell italic muted={row.aht.combinedSec == null}>{formatSeconds(row.aht.combinedSec)}</Cell>
                    <Cell left bold italic muted={!p.need}><Fte value={p.need} showHours={showHours} /></Cell>
                    <Cell left bold italic muted={!p.scheduled}><Fte value={p.scheduled} showHours={showHours} /></Cell>
                    <Cell left bold italic tone={gapTone(p.gap)} muted={p.gap == null}>{formatGap(p.gap)}</Cell>
                  </TableRow>
                )
              })}
            </>
          )}
        </TableBody>
      </Table>
    </Paper>
  )
}

function ServiceTable({ report }) {
  const rows = report.slaRows
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', ...TABLE_BOX }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head align="left">Week ending</Head>
            {rows.map((r, i) => <Head key={r.key} left={i === 0}>{slaHeader(r)}</Head>)}
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={rows.length + 1}>Average time to first answer · recent weeks, oldest first</Divider>
          {report.weeks.map((week) => (
            <TableRow key={week.week} sx={rowSx(week)}>
              <WeekCell row={week} />
              {rows.map((r, i) => {
                const s = week.service[r.key]
                return (
                  <Cell key={r.key} left={i === 0} tone={slaTone(s?.avgHours, s?.targetHours)} muted={s?.avgHours == null} italic={week.isPartial}>
                    {s?.avgHours == null ? EM_DASH : formatAvgHours(s.avgHours)}
                  </Cell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

// The loaded view, apart from the fetch so it can be rendered from a report alone.
export function ForecastEmailView({ report }) {
  const [showHours, setShowHours] = useState(false)
  const a = report.assumptions
  return (
    <Box>
      <SectionHeading variant="subtitle1" sx={{ mb: 1, ...TABLE_BOX }} title="Forecasting: Email" descriptionMaxWidth={1100}>
        What happened, week by week, oldest first. Actuals only so far, no forecast. One volume line for the customer email your team
        works: CXHub (Website, Internal notices, eBay; spam out) plus the Bright Pattern Amazon and Spanish Email queues, with the Bright Pattern share
        shown, so a queue moving over to CXHub moves volume between the two columns and not the total. Support email is its own view.
      </SectionHeading>
      <FormControlLabel
        control={<Switch size="small" checked={showHours} onChange={(e) => setShowHours(e.target.checked)} />}
        label="Show hours under each FTE"
        sx={{ mb: 1 }}
      />
      {report.warnings.includes('thin_forecast_baseline') && (
        <Alert severity="info" sx={{ mb: 1, maxWidth: 1100 }}>
          The forecast rests on {report.forecast.baseline.weeks.length} clean week{report.forecast.baseline.weeks.length === 1 ? '' : 's'} since email moved to CXHub
          ({report.forecast.baseline.weeks.map((w) => w.slice(5)).join(', ')}), so it is thin and firms up as weeks are added.
        </Alert>
      )}
      {report.warnings.includes('no_forecast_baseline') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          There is no clean recent week to forecast from yet, so no forecast is shown.
        </Alert>
      )}
      {report.warnings.includes('bp_amazon_missing') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          Schedule Hub returned no Bright Pattern email (Amazon, Spanish), so the line is CXHub alone.
        </Alert>
      )}

      <Box sx={{ mt: 2, ...TABLE_BOX }}>
        <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="1. Volume" descriptionMaxWidth={1100}>
          CXHub counts the contacts that arrived in the week, spam out (the Weekly Report's valid count). Bright Pattern is the answered
          Amazon contacts, 1A and AM, across their three queues each, plus the 1A and AM Spanish Email queues, which now hold only Amazon emails written in Spanish (other Spanish email is worked in CXHub with the Translate button and counts in the CXHub column). Emails handled is the part of the total that took agent time
          (CXHub contacts a person handled, plus the Bright Pattern answered): the count the FTE needed is worked from, so the rest
          were automated or closed by the system. This week is only so far. The forecast rows (italic) are CXHub's level from the clean
          recent weeks since email moved over (up to {a.baselineWeeks}, skipping event weeks), with the week's holiday multiplier on top
          (a week not in the list is 1.0; the list and what it rests on are under Holiday multipliers below the table, and you edit it). A
          sustained rise or fall in the actuals moves the level within a few weeks; one odd week is diluted by the others. Bright Pattern
          is held flat at its recent weekly average.
        </SectionHeading>
        <VolumeTable report={report} />
        {report.forecast?.multipliers?.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <SectionHeading variant="subtitle2" appearance="quiet" title="Holiday multipliers" descriptionMaxWidth={1100}>
              Each listed week's forecast is the level times Used; any other week is 1.0. Used starts at the typical of the three years
              beside it: Bright Pattern's answered customer email (every queue except Support, outbound and the Spanish callback) that
              week against a normal early-autumn week, lined up by the holiday. Customer email falls in Thanksgiving week and Christmas
              week every year, and there is no Black Friday or Cyber Monday bump. 2025's offered count is not used, because Bright Pattern
              began logging 700 to 2,000 "other unanswered" emails a week from 11/08/2025, a change in measurement. The numbers are in
              EMAIL_PLAN (weekMultipliers) and are yours to change.
            </SectionHeading>
            <MultiplierTable rows={report.forecast.multipliers} />
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 3, ...TABLE_BOX }}>
        <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="2. Staffing" descriptionMaxWidth={1100}>
          The work is the contacts a person handled (CXHub's handled and still-waiting contacts, plus Bright Pattern's answered) times
          handle time. CXHub's handle time is the Reporting one (claim to action, pauses out, spam out) with each claim held to {a.ahtCapMin} minutes,
          so a few claims left open for hours do not move a week; the no-cap column is the plain average Reporting shows, and the next
          column counts the claims the cap cut. Bright Pattern's is its raw handle
          time divided by {a.bpInFocusDivisor}, because it counts time the agent was not looking at the email; that is an estimate until the Power BI
          in-focus figures are in. FTE needed is that work ÷ {a.onQueueHoursPerFte} hours × {1 + a.shrinkageRate}, your {Math.round(a.shrinkageRate * 100)}% standard with no flat spare.
          FTE scheduled is the CXHub email time in the schedule: for {a.scheduleTeams.join(', ')}, scheduled hours × the share that is CXHub
          blocks, ÷ {a.scheduledHoursPerFte}, plus the Bright Pattern time Nick Email has left after chat (where the Amazon work is done). A week with no CXHub email blocks scheduled (before they existed, or past the roster) has no scheduled FTE or gap.
          The forecast need is the forecast people-handled contacts (the baseline's share of the forecast volume) times the baseline's
          handle time, the same way, against the roster where Schedule Hub has one.
        </SectionHeading>
        <StaffingTable report={report} showHours={showHours} />
      </Box>

      <Box sx={{ mt: 3, ...TABLE_BOX }}>
        <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="3. Service" descriptionMaxWidth={1100}>
          Average time from a contact arriving to its first answer, in hours, from the Reporting ASA, against each account's target:
          12 hours, and 24 for AM eBay and AM Website. 1A Email and AM Email each pool the brand's Website and Internal-notice accounts, and
          AM Email's target is AM's 24 hours. Bright Pattern has no usable email service level (it always reads 100%), so Amazon has none.
        </SectionHeading>
        <ServiceTable report={report} />
      </Box>

      <SectionHeading variant="subtitle2" appearance="quiet" sx={{ mt: 2 }} title="Colors and notes" descriptionMaxWidth={1100}>
        Colors: gap red below 0, amber under +1; speed of answer red over the target, amber within 20% of it. "—" means no data yet, never
        zero. Italic is this week so far.
      </SectionHeading>
    </Box>
  )
}

export default function ForecastEmail() {
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let live = true
    fetchForecastEmail()
      .then((r) => { if (live) setReport(r) })
      .catch((e) => { if (live) setError(e.message) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [])

  if (loading) return <Box sx={{ py: 2 }}><CircularProgress size={24} /></Box>
  if (error) return <Alert severity="error">{error}</Alert>
  if (!report) return null
  return <ForecastEmailView report={report} />
}
